import {
	Anchor,
	Box,
	Card,
	Flex,
	Group,
	Stack,
	Tooltip,
	useMantineTheme,
} from '@mantine/core';
import type { AiChatListItem } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { Selector, SelectorTarget } from '@repo/common/components/Selectors';
import { Icon, Text } from '@repo/foundations';

import { SelectType } from '@repo/common/components/Selectors/types';
import { UserAvatar } from '@repo/common/components/UserAvatar';
import React, { memo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { useAuthUser } from '../../../api';
import { AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT, SORT_OPTIONS } from '../constants';
import type {
	AiChatListComponentProps,
	AiChatListContentProps,
	AiChatSortOrder,
} from '../types';
import { formatRelativeTime } from '../utils';
import { AiImpactWidgetEmpty } from './AiImpactWidgetEmpty';
import { AiImpactWidgetSkeleton } from './AiImpactWidgetSkeleton';
import { useStyles } from './styles';

const AiChatListContent = memo(
	({
		items,
		sortOrder,
		onSortChange,
		theme,
		badgeComponent,
		hasNextPage,
		isFetching,
		onLoadMore,
		error,
		dateFilter,
	}: AiChatListContentProps) => {
		const navigate = useNavigate();
		const { classes } = useStyles();
		const { user, workspace } = useAuthUser();

		const handleSortChange = useCallback(
			(values: string[]) => {
				const newSortOrder = values[0] as AiChatSortOrder;
				if (newSortOrder && newSortOrder !== sortOrder) {
					onSortChange(newSortOrder);
				}
			},
			[onSortChange, sortOrder]
		);

		const handleChatClick = useCallback(
			(chat: AiChatListItem) => {
				trackEvent(
					'ai_impact_dashboard/chat_row_clicked',
					{
						chat_id: chat.id,
						quality_score: (chat.score ?? 0).toString(),
						widget_type: 'ai_chats',
						sort_order: sortOrder,
						current_date_filter: dateFilter ?? '30d',
					},
					user,
					workspace
				);

				navigate(`/analytics/ai-chats?chatId=${chat.id}`);
			},
			[navigate, sortOrder, dateFilter, user, workspace]
		);

		const itemContent = useCallback(
			(index: number, chat: AiChatListItem) => (
				<Box
					className={classes.card}
					pb="md"
					onClick={() => handleChatClick(chat)}
				>
					<Flex justify="space-between" align="center">
						<Flex
							justify="start"
							align="center"
							gap="sm"
							className={classes.avatarInfo}
						>
							<UserAvatar
								size="md"
								user={{
									id: chat.user_id,
									display_name: chat.user_name || 'Unknown user',
									first_name: '',
									last_name: '',
									profile_picture: (
										chat as typeof chat & {
											user_profile_picture?: string;
										}
									).user_profile_picture,
								}}
							/>
							<Stack spacing={0} className={classes.avatarInfo}>
								<Text
									size="sm"
									color="text/primary/default"
									weight="semibold"
									lineClamp={1}
								>
									{chat.title || chat.prompt}
								</Text>
								<Text size="xs" color="text/secondary/default" lineClamp={1}>
									{`Asked by ${chat.user_name || 'Unknown user'} · ${formatRelativeTime(chat.created_at)}`}
								</Text>
							</Stack>
						</Flex>
						<Box style={{ flexShrink: 0 }}>
							{chat.score &&
								React.createElement(badgeComponent, { score: chat.score })}
						</Box>
					</Flex>
				</Box>
			),
			[classes.card, classes.avatarInfo, handleChatClick, badgeComponent]
		);

		if ((items.length === 0 && !isFetching) || error) {
			return (
				<AiImpactWidgetEmpty
					theme={theme}
					message={
						error
							? 'Error loading AI chats'
							: 'No AI chats found for the selected time period'
					}
					icon={error ? 'exclamationCircle' : 'messageCircle'}
				/>
			);
		}

		return (
			<Stack spacing={0}>
				<Group position="apart" pt="sm" pr="sm" pb="sm" pl="md">
					<Group>
						<Icon name="messageCircle" size="md" color="text/primary/default" />
						<Text size="sm" color="text/primary/default" fw={600}>
							AI chats
						</Text>
					</Group>
					<Selector
						selectType={SelectType.Single}
						initialValue={[sortOrder]}
						onChange={handleSortChange}
						getItems={async () => SORT_OPTIONS}
						disableSearch
					>
						<SelectorTarget size="md" variant="default">
							{SORT_OPTIONS.find((opt) => opt.value === sortOrder)?.label}
						</SelectorTarget>
					</Selector>
				</Group>
				<Box
					mih={AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT}
					h={AI_IMPACT_DASHBOARD_WIDGETS_HEIGHT}
				>
					<Virtuoso
						style={{ height: '100%' }}
						data={items}
						endReached={() => {
							if (hasNextPage && !isFetching) {
								onLoadMore();
							}
						}}
						itemContent={itemContent}
					/>
				</Box>
			</Stack>
		);
	}
);

export const AiChatList = memo(
	({
		items,
		onSortChange,
		sortOrder,
		isLoading,
		error,
		badgeComponent,
		handleSeeAllClick,
		hasNextPage,
		isFetching,
		onLoadMore,
		dateFilter,
	}: AiChatListComponentProps) => {
		const theme = useMantineTheme();

		if (isLoading && items.length === 0) {
			return <AiImpactWidgetSkeleton />;
		}

		return (
			<Card
				p={theme.spacing.xs}
				withBorder
				style={{ borderRadius: theme.radius.md }}
			>
				<Stack spacing={0}>
					<AiChatListContent
						items={items}
						sortOrder={sortOrder}
						onSortChange={onSortChange}
						theme={theme}
						badgeComponent={badgeComponent}
						hasNextPage={hasNextPage}
						isFetching={isFetching}
						onLoadMore={onLoadMore}
						error={error}
						dateFilter={dateFilter}
					/>
					<Group position="apart" pt="lg" pr="sm" pb="md" pl="md">
						<Flex align="center" gap="xxs">
							<Text size="xs" color="text/primary/default">
								How is scoring calculated?
							</Text>
							<Tooltip
								style={{
									maxWidth: 300,
									whiteSpace: 'pre-wrap',
									width: '300px !important',
								}}
								label="After each AI response, we automatically evaluate the quality. Some of the criteria includes if AI answers the user's question or how accurate and helpful the response was."
							>
								<Icon name="infoCircle" color="text/secondary/default" />
							</Tooltip>
						</Flex>
						<Anchor
							color={theme.other.getColor('text/emphasis/default')}
							weight="semibold"
							fz="sm"
							onClick={handleSeeAllClick}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: theme.spacing['2xs'],
							}}
						>
							<Text size="sm" color="text/emphasis/default">
								View more
							</Text>
							<Icon name="chevronRight" color="text/emphasis/default" />
						</Anchor>
					</Group>
				</Stack>
			</Card>
		);
	}
);
